import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { IconClose } from '@/components/ui/icons'
import { Switch } from '@/components/ui/switch'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'
import { cn } from '@/lib/utils'

const LEARN_MORE_URL = 'https://www.uni-corvinus.hu/'

/** Figma `panel-list` — text column plus an optional trailing control. */
function PanelRow({
  title,
  description,
  control,
  last = false,
}: {
  title: string
  description: ReactNode
  control?: ReactNode
  last?: boolean
}): ReactElement {
  return (
    <div
      data-name="panel-list"
      className={cn('flex w-full items-center gap-4 pb-4', !last && 'hub-rule')}
    >
      <div className="text-list-text flex min-w-px flex-1 flex-col items-start gap-1">
        <p className="hub-title-lg w-full">{title}</p>
        <div className="hub-body-sm w-full">{description}</div>
      </div>
      {control}
    </div>
  )
}

interface CookieSettingsDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Cookie Preferences Center — Figma `cookies` (2256:282019 and the
 * 1200/900/600/400 frames). 400px wide, or the viewport minus 48px below that,
 * always centred over a blurred 20% overlay.
 */
export function CookieSettingsDialog({
  open,
  onClose,
}: CookieSettingsDialogProps): ReactElement | null {
  const { t } = useTranslation()
  const preferences = useCookieConsentStore((state) => state.preferences)
  const save = useCookieConsentStore((state) => state.save)

  const [analytics, setAnalytics] = useState(preferences.analytics)
  const [marketing, setMarketing] = useState(preferences.marketing)

  // Re-sync when the dialog is reopened.
  useEffect(() => {
    if (!open) return
    setAnalytics(preferences.analytics)
    setMarketing(preferences.marketing)
  }, [open, preferences])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  const commit = (next: { analytics: boolean; marketing: boolean }): void => {
    save(next)
  }

  return (
    <>
      <div
        data-node-id="2256:282018"
        data-name="bg-overlay"
        className="bg-overlay fixed inset-0 z-50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        data-node-id="2256:282019"
        data-name="cookies"
        role="dialog"
        aria-modal="true"
        aria-label={t('cookies.centerTitle')}
        className="hub-surface-cookies-panel fixed top-1/2 left-1/2 z-50 flex w-[calc(100%-48px)] max-w-[400px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-4 rounded-md p-4"
      >
        <PanelRow
          title={t('cookies.centerTitle')}
          description={
            <Trans
              i18nKey="cookies.centerDescription"
              components={{
                learn: (
                  <a
                    className="hub-link"
                    href={LEARN_MORE_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                  />
                ),
              }}
            />
          }
        />

        <PanelRow
          title={t('cookies.essentialTitle')}
          description={t('cookies.essentialDescription')}
          control={<Switch checked disabled label={t('cookies.essentialTitle')} />}
        />

        <PanelRow
          title={t('cookies.analyticsTitle')}
          description={t('cookies.analyticsDescription')}
          control={
            <Switch
              checked={analytics}
              label={t('cookies.analyticsTitle')}
              onCheckedChange={(value) => {
                setAnalytics(value)
                commit({ analytics: value, marketing })
              }}
            />
          }
        />

        <PanelRow
          last
          title={t('cookies.marketingTitle')}
          description={t('cookies.marketingDescription')}
          control={
            <Switch
              checked={marketing}
              label={t('cookies.marketingTitle')}
              onCheckedChange={(value) => {
                setMarketing(value)
                commit({ analytics, marketing: value })
              }}
            />
          }
        />

        <button
          type="button"
          onClick={onClose}
          aria-label={t('actions.close')}
          data-node-id="2256:282024"
          className="text-btn-icon hover:bg-btn-secondary-raised absolute top-[8px] right-[8px] flex items-center justify-center rounded-xs p-1.5 transition-colors"
        >
          <IconClose className="size-[16px]" aria-hidden="true" />
        </button>
      </div>
    </>
  )
}
